import { FC, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { TabsProvider } from "../../context/tabsContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useI18n } from "../../hooks/useTranslations";
import { useSuppliersApi } from "../../http/suppliers/api";
import { UpsertSupplier } from "../../http/suppliers/types";
import SupplierDocuments from "../supplierDocuments/SupplierDocuments";
import Header from "../ui/Header";
import Tabs, { TabContent, TabsContents } from "../ui/Tabs";
import SupplierForm, { SupplierFormBody } from "./SupplierForm";

const EditSupplier: FC = () => {
  const i18n = useI18n();

  return (
    <>
      <Header title={i18n.t.suppliers.edit} />
      <TabsProvider>
        <TabbedEditSupplier />
      </TabsProvider>
    </>
  );
};

export default EditSupplier;

const TabbedEditSupplier: FC = () => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const suppliersApi = useSuppliersApi();
  const aborter = useRef(new AbortController());

  const { id } = useParams<{ id: string }>();

  const supplierSwr = suppliersApi.getAdminSupplier(id!, aborter.current.signal);
  const [supplier, isInitialized] = useDataInitializer(supplierSwr.data, supplierSwr.error);

  useEffect(() => {
    id && supplierSwr.trigger();
  }, [id]);

  useEffect(() => {
    supplierSwr.error && dispatch(showErrorNotification(i18n, supplierSwr.error));
  }, [supplierSwr.error]);

  const onSubmit = async (formData: SupplierFormBody): Promise<void> => {
    if (supplier?.id) {
      const body: UpsertSupplier = {
        name: formData.name,
        address: formData.address,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        currentVersionNumber: supplier.versionNumber,
      };

      setIsSubmitting(true);
      try {
        await suppliersApi.updateAdminSupplier(supplier.id, body);
        dispatch(showSuccessNotification(i18n.t));
        supplierSwr.trigger();
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const tabs = [{ title: i18n.t.common.edit }, { title: i18n.t.common.documents }];

  const tabComponents: TabContent[] = [
    {
      body: (
        <SupplierForm
          onSubmit={onSubmit}
          isDisabled={isSubmitting || !supplier || !userSettings.permissions.suppliers.update}
          isLoading={!isInitialized}
          supplier={supplier}
        />
      ),
      eager: true,
      withBorder: true,
    },
    { body: <SupplierDocuments setIsDragging={setIsDragging} />, withBorder: true },
  ];

  return (
    <>
      <Tabs tabs={tabs} />
      <TabsContents contents={tabComponents} isDragging={isDragging} />
    </>
  );
};
